// =============================================================================
// hooks/useSyncHistoryAlert.ts — Avisa o vendedor quando o WhatsApp está
// conectado mas o histórico não veio (F50-S02).
//
// O caso real que isto resolve: a Valéria pareou o aparelho, a importação
// voltou vazia porque o GOWS ainda não tinha baixado o histórico, e NADA na
// tela contou isso a ela — `errors_count = 0`, status `completed`. Ela seguiu
// trabalhando um dia inteiro sem 86 conversas e 2.921 mensagens que existiam.
//
// O F50-S01 (worker periódico) já retoma a importação vazia sozinho, sem tela
// aberta — mas com teto de `SYNC_MAX_ATTEMPTS` tentativas. Este hook só alarma
// DEPOIS que o teto estoura: antes disso, resultado vazio é "ainda não", é a
// janela normal logo após parear (o GOWS pode levar minutos para entregar o
// histórico). Alarmar antes do teto seria falso alarme na situação esperada.
//
// "Não repetir a cada carregamento" (DoD): o aviso é marcado como "visto" em
// `sessionStorage` assim que aparece — não reaparece em outra navegação/
// remontagem da mesma aba, mesmo sem o vendedor apertar nada. Fechar o
// navegador (nova sessão) ou o problema se resolver (novo canal, ou a
// importação finalmente trouxe conteúdo) libera o aviso de novo.
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { z } from 'zod';

import { CONNECT_PROMPT_PERMISSION } from '../features/meu-whatsapp/connectPrompt';
import { api } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

import { useFeatureFlag } from './useFeatureFlag';

// ---------------------------------------------------------------------------
// Regra pura — sem React, fácil de testar
// ---------------------------------------------------------------------------

/**
 * Teto de tentativas do worker de retomada (F50-S01).
 *
 * Espelha `SYNC_MAX_ATTEMPTS` de `apps/api/src/modules/channels/
 * whatsapp-sync.service.ts`. Duplicado aqui — e não importado — porque o
 * frontend não compartilha runtime com a API; documentado para que quem mudar
 * o teto do lado do backend saiba que precisa espelhar aqui.
 */
export const FRONTEND_SYNC_MAX_ATTEMPTS = 6;

export type ChannelPairingStatus = 'STOPPED' | 'STARTING' | 'SCAN_QR_CODE' | 'WORKING' | 'FAILED';
export type SyncRunStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface SyncHistoryAlertInput {
  readonly channelStatus: ChannelPairingStatus;
  readonly syncStatus: SyncRunStatus | null;
  readonly chatsTotal: number | null;
  readonly syncAttempts: number;
}

/**
 * Decide se o aviso "seu histórico não veio" cabe.
 *
 * - Canal precisa estar `WORKING` — sem aparelho conectado não há nada a
 *   avisar (DoD: "usuário sem aparelho conectado não vê nada").
 * - Precisa existir um resultado — `syncStatus === null` é "nunca rodou",
 *   estado inicial normal logo após parear.
 * - Resultado com conteúdo (`completed` e `chatsTotal > 0`) nunca alarma —
 *   deu certo (DoD: "sem falso alarme").
 * - `pending`/`running` nunca alarma — a importação está em voo.
 * - Só `completed` com `chatsTotal === 0`, ou `failed`, são candidatos — e
 *   mesmo esses só alarmam quando `syncAttempts` já bateu o teto do worker:
 *   antes disso, é a janela normal de retomada automática.
 */
export function shouldWarnAboutMissingHistory({
  channelStatus,
  syncStatus,
  chatsTotal,
  syncAttempts,
}: SyncHistoryAlertInput): boolean {
  if (channelStatus !== 'WORKING') return false;
  if (syncStatus === null) return false;
  if (syncStatus === 'pending' || syncStatus === 'running') return false;

  const isEmptyOrFailed = syncStatus === 'failed' || (chatsTotal ?? 0) === 0;
  if (!isEmptyOrFailed) return false;

  return syncAttempts >= FRONTEND_SYNC_MAX_ATTEMPTS;
}

// ---------------------------------------------------------------------------
// Persistência "visto nesta sessão" — sessionStorage, por canal
// ---------------------------------------------------------------------------

function seenStorageKey(channelId: string): string {
  return `bdp:sync-history-alert-seen:${channelId}`;
}

/** Lê se este canal já mostrou o aviso na aba atual. Falha aberta para `false` (nunca bloqueia um alarme real por erro de storage). */
export function hasBeenSeenThisSession(channelId: string): boolean {
  try {
    return window.sessionStorage.getItem(seenStorageKey(channelId)) === '1';
  } catch {
    return false;
  }
}

export function markSeenThisSession(channelId: string): void {
  try {
    window.sessionStorage.setItem(seenStorageKey(channelId), '1');
  } catch {
    // sessionStorage indisponível (modo privado/quota) — degrada para "sempre mostra".
    // Melhor mostrar de novo do que nunca ter mostrado o alarme real.
  }
}

// ---------------------------------------------------------------------------
// Schema — só o que este hook precisa de GET /api/channels/waha/me
// ---------------------------------------------------------------------------

const MyPairingAlertSchema = z.object({
  channel_id: z.string().uuid().nullable(),
  status: z.enum(['STOPPED', 'STARTING', 'SCAN_QR_CODE', 'WORKING', 'FAILED']),
  sync: z
    .object({
      status: z.enum(['pending', 'running', 'completed', 'failed']),
      chats_total: z.number().int(),
    })
    .nullable(),
  sync_attempts: z.number().int(),
});

// ---------------------------------------------------------------------------
// Query
// ---------------------------------------------------------------------------

export const SYNC_HISTORY_ALERT_QUERY_KEY = ['sync-history-alert', 'me'] as const;

const PAIRING_FLAG = 'whatsapp_sync.pairing.enabled';

/**
 * Intervalo de checagem. Não precisa da agressividade do polling da tela de
 * pareamento (3s) — este hook roda em segundo plano, na tela onde o vendedor
 * de fato trabalha, e o cenário que detecta já é "o worker esgotou o teto",
 * portanto não muda de um segundo para o outro.
 */
const POLL_INTERVAL_MS = 30_000;

/**
 * Intervalo do refetch. Só repete enquanto a última leitura deu certo e trouxe
 * dados: depois de 404 (sem agente vinculado), 403 (preso ao primeiro acesso)
 * ou qualquer erro, o polling PARA — repetir só enchia o console de 4xx a cada
 * 30 s sem nunca mudar o desfecho (F58-S14, UX-04). Volta quando o hook é
 * remontado ou a flag/permissão muda (a query é recriada).
 */
export function resolveSyncAlertRefetchInterval(state: {
  readonly status: 'pending' | 'error' | 'success';
  readonly hasData: boolean;
}): number | false {
  if (state.status === 'error') return false;
  if (!state.hasData) return false;
  return POLL_INTERVAL_MS;
}

export interface SyncHistoryAlertState {
  /** `true` quando o pop-up deve estar aberto. */
  readonly open: boolean;
  readonly channelId: string | null;
  /** Fecha o aviso (Escape, botão fechar, ou navegação para o pareamento). */
  readonly dismiss: () => void;
}

/**
 * Hook de alto nível: busca o estado de pareamento/importação do vendedor
 * logado e decide se o aviso de histórico ausente deve aparecer.
 *
 * Não faz nada para quem não tem `channel.self_pair`, não é vendedor (sem agente
 * vinculado) ou está com a flag `whatsapp_sync.pairing.enabled` desligada — o `GET /waha/me` responde 404
 * nesses casos, e o hook trata isso como "nada a avisar", não como erro para
 * mostrar na tela.
 */
export function useSyncHistoryAlert(): SyncHistoryAlertState {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { enabled: flagEnabled } = useFeatureFlag(PAIRING_FLAG);
  // Mesma permissão do convite "conecte o seu WhatsApp": quem não pode parear
  // o próprio aparelho não tem o que avisar, e a rota responderia 403/404.
  const canSelfPair = useAuthStore((s) => s.hasPermission)(CONNECT_PROMPT_PERMISSION);

  const query = useQuery({
    queryKey: SYNC_HISTORY_ALERT_QUERY_KEY,
    queryFn: async () => MyPairingAlertSchema.parse(await api.get('/api/channels/waha/me')),
    enabled: isAuthenticated && flagEnabled && canSelfPair,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (q) =>
      resolveSyncAlertRefetchInterval({
        status: q.state.status,
        hasData: q.state.data !== undefined,
      }),
  });

  const data = query.data;
  const channelId = data?.channel_id ?? null;

  const shouldWarn =
    data !== undefined &&
    shouldWarnAboutMissingHistory({
      channelStatus: data.status,
      syncStatus: data.sync?.status ?? null,
      chatsTotal: data.sync?.chats_total ?? null,
      syncAttempts: data.sync_attempts,
    });

  // `dismissed` só é setado por: (1) leitura do sessionStorage feita UMA vez
  // por canal (efeito abaixo), ou (2) chamada explícita de `dismiss()`. Nunca
  // é recalculado a cada render — é isso que evita o aviso "piscar" fechado
  // no instante em que ele mesmo grava o "visto" no sessionStorage.
  const [dismissed, setDismissed] = React.useState(false);
  const initializedForChannelRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (channelId === null) return;
    if (initializedForChannelRef.current === channelId) return;
    initializedForChannelRef.current = channelId;
    setDismissed(hasBeenSeenThisSession(channelId));
  }, [channelId]);

  const open = shouldWarn && channelId !== null && !dismissed;

  // Assim que o aviso é exibido, marca "visto" — não depende do vendedor
  // interagir. Não altera `dismissed`, então não fecha o próprio aviso que
  // acabou de abrir.
  React.useEffect(() => {
    if (open && channelId !== null) {
      markSeenThisSession(channelId);
    }
  }, [open, channelId]);

  const dismiss = React.useCallback(() => {
    setDismissed(true);
    if (channelId !== null) markSeenThisSession(channelId);
  }, [channelId]);

  return { open, channelId, dismiss };
}
